import React from 'react';
import { GoogleMap, DirectionsRenderer, Marker } from '@react-google-maps/api';
import { useEffect, useState } from 'react';
import _map from 'lodash/map';
import { Box, Button, Card } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LazyLoadScript from 'src/components/LazyLoadScript';

const containerStyle = {
  width: '100%',
  height: '100%',
};

const mapOptions = {
  streetViewControl: false,
  mapTypeControl: false,
};

function buildMarkerIcon(marker = {}) {
  if (!window.google?.maps?.SymbolPath) return undefined;
  const isPickup = marker.type === 'pickup';
  const statusColors = {
    completed: '#2e7d32',
    delivered: '#2e7d32',
    picked_up: '#ed6c02',
    en_route: '#ed6c02',
    assigned: '#0288d1',
    available: '#0288d1',
    missed: '#d32f2f',
    needs_follow_up: '#d32f2f',
    canceled: '#d32f2f',
  };
  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: isPickup ? '#1565c0' : statusColors[marker.status] || '#637381',
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 2,
    scale: isPickup ? 11 : 9,
  };
}

function DirectionsMap({
  sourceLocation,
  destinationLocation,
  waypoints,
  optimizeWaypoints,
  suppressMarkers,
  markers,
  onDirections,
}) {
  // eslint-disable-next-line no-unused-vars
  const [map, setMap] = React.useState(null);
  const [directionResult, setDirectionResult] = useState({});
  const onLoad = React.useCallback(function callback(map) {
    const bounds = new window.google.maps.LatLngBounds();
    map.fitBounds(bounds);
    setMap(map);
  }, []);

  // eslint-disable-next-line no-unused-vars
  const onUnmount = React.useCallback(function callback(_map) {
    setMap(null);
  }, []);

  useEffect(() => {
    const DirectionsService = new window.google.maps.DirectionsService();
    if (sourceLocation.lat && destinationLocation.lat) {
      setDirectionResult({});
      DirectionsService.route(
        {
          origin: new window.google.maps.LatLng(
            sourceLocation.lat,
            sourceLocation.lng
          ),
          destination: new window.google.maps.LatLng(
            destinationLocation.lat,
            destinationLocation.lng
          ),
          waypoints: _map(waypoints, (waypoint) => ({
            location: new window.google.maps.LatLng(waypoint.lat, waypoint.lng),
          })),
          optimizeWaypoints,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK) {
            setDirectionResult(result);
            onDirections?.(result);
          } else {
            console.error(`error fetching directions ${result}`);
          }
        }
      );
    }
  }, [
    destinationLocation,
    optimizeWaypoints,
    onDirections,
    sourceLocation,
    waypoints,
  ]);

  return (
    <GoogleMap
      mapContainerClassName="map-container"
      mapContainerStyle={containerStyle}
      onLoad={onLoad}
      onUnmount={onUnmount}
      zoom={10}
      options={mapOptions}
    >
      {Array.isArray(directionResult?.routes) ? (
        <DirectionsRenderer
          directions={directionResult}
          options={{ suppressMarkers }}
        />
      ) : null}
      {suppressMarkers
        ? (markers || []).map((marker, index) => (
            <Marker
              key={`${marker.type || 'marker'}-${marker.label || index}`}
              position={{ lat: marker.lat, lng: marker.lng }}
              label={
                marker.label
                  ? {
                      text: marker.label,
                      color: '#ffffff',
                      fontWeight: '700',
                    }
                  : undefined
              }
              icon={buildMarkerIcon(marker)}
            />
          ))
        : null}
    </GoogleMap>
  );
}

function MyComponent({
  sourceLocation,
  destinationLocation,
  waypoints,
  optimizeWaypoints = false,
  suppressMarkers = false,
  markers = [],
  onDirections,
  showImmediately = false,
}) {
  const { t } = useTranslation('translation');
  const [show, setShowing] = useState(showImmediately);

  useEffect(() => {
    if (showImmediately) {
      setShowing(true);
    }
  }, [showImmediately]);

  return (
    <Box
      component={Card}
      width="100%"
      height="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      position="relative"
      overflow="hidden"
      sx={{
        border: (t) => !show && `1px solid ${t.palette.divider}`,
        '& .map-container': { width: '100%', height: '100%' },
      }}
    >
      {show ? (
        <LazyLoadScript>
          <DirectionsMap
            sourceLocation={sourceLocation}
            destinationLocation={destinationLocation}
            waypoints={waypoints}
            optimizeWaypoints={optimizeWaypoints}
            suppressMarkers={suppressMarkers}
            markers={markers}
            onDirections={onDirections}
          />
        </LazyLoadScript>
      ) : (
        <>
          <img
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              width: '100%',
              height: '100%',
              filter: 'blur(5px)',
              opacity: 0.6,
            }}
            src="/static/images/map-image.png"
            alt=""
          />
          <Button
            // color="secondary"
            onClick={() => setShowing(true)}
            variant="outlined"
          >
            {t('donations.view-directions')}
          </Button>
        </>
      )}
    </Box>
  );
}

export default React.memo(MyComponent);
